import React, { useState } from 'react';
import { View, ScrollView, StyleSheet, Modal, TextInput } from 'react-native';
import { Card, Typography, Button, useTheme, ProgressBar, Icons, Dropdown } from '@hmsexp/component-library';
import InfoSection from './InfoSection';
import Activities from './Activities';
import get from 'lodash/get';
import { addFeedBackNote } from '../actions/addFeedBackNote';
import { updateAlert } from '../../Alerts/actions/updateAlert';
import { fetchfeedbackdetailsdata } from '../actions/fetchFeedBackDetailsData';
import { useDispatch } from 'react-redux';
import { useWindowDimensions } from 'react-native';
import OtaIcon from '../../../components/Common/EmojiMap/OtaIcon';
import EmojiMap from '../../../components/Common/EmojiMap';
import FeedDetailsLoader from './Loader';
import { emojiSubIcon } from '../../OnlineReview/components/OnlineReviewComp/components/SentimentChart/utils';
import { getStyles } from './styles';

const FeedDetails = ({
  feedback_details,
  feedback_widget_details,
  role,
  feedback_id,
  note_response,
  alert_response,
  isLoading
}) => {
  const { palette } = useTheme();
  const dispatch = useDispatch();
  const detail = feedback_details;
  const feedbackType = detail?.system_info?.feedback_type;
  const feedbackSource = detail?.system_info?.feedback_source;
  const alerts = detail?.activity?.alerts;
  const normalizedFeedBackType = feedbackType?.toLowerCase().replace(/[^a-z0-9]/g, '');
  const segments = detail?.segments;
  const ratings = detail?.ratings;
  const blacklisted_ratings = detail?.blacklisted_ratings;
  const rating_scale_ten = detail?.rating_scale_ten;
  const hasSegments = segments && Object.keys(segments).length > 0;
  const [noteVisible, setNoteVisible] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [addNoteLoading, setAddNoteloading] = useState(false);
  const [closeAlertVisible, setCloseAlertVisible] = useState(false);
  const [closeAlertNoteText, setCloseAlertNoteText] = useState('');
  const [closeAlertloading, setCloseAlertloading] = useState(false);
  const [reopenAlertVisible, setReopenAlertVisible] = useState(false);
  const [reopenAlertNoteText, setReopenAlertNoteText] = useState('');
  const [reopenAlertloading, setReopenAlertloading] = useState(false);
  const [alertType, setAlertType] = useState([]);
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const OPEN_STATUSES = new Set(['NEW', 'IN PROGRESS', 'OVERDUE']);
  const CLOSED_STATUS = 'CLOSED';

  const hasOpenAlert = alerts?.some(alert => OPEN_STATUSES.has(alert?.status));
  const hasClosedAlert = alerts?.some(alert => alert?.status === CLOSED_STATUS);

  const closeAlertOptions = React.useMemo(() => {
    if (!Array.isArray(alerts) || alerts.length === 0) return [];

    return Array.from(
      new Map(
        alerts
          .filter(alert => OPEN_STATUSES.has(alert?.status))
          .map(alert => [alert?.type, { label: alert?.type, value: alert?.type }])
      ).values()
    );
  }, [alerts]);

  const reopenAlertOptions = React.useMemo(() => {
    if (!Array.isArray(alerts) || alerts.length === 0) return [];

    return Array.from(
      new Map(
        alerts
          .filter(alert => alert?.status === CLOSED_STATUS)
          .map(alert => [alert?.type, { label: alert?.type, value: alert?.type }])
      ).values()
    );
  }, [alerts]);

  const styles = getStyles(isMobile);
  const getEmojiType = score => {
    const num = Number(score);
    if (num >= 4.0) return 'HAPPY';
    if (num >= 3) return 'NEUTRAL';
    return 'SAD';
  };

  const formatToLongDate = arr => {
    if (!arr) return '';

    const date = new Date(arr[0], arr[1] - 1, arr[2], arr[3], arr[4]);

    const formatted = date.toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    return formatted.replace(',', ' at');
  };

  let fields;
  if (role === 'muo') {
    fields = get(feedback_widget_details, 'contents.info_section.personal_info_multi_unit', []) || [];
  } else {
    fields = get(feedback_widget_details, 'contents.info_section.personal_info_property', []) || [];
  }

  const addNote = (id, note, hotel_id) => {
    setAddNoteloading(true);

    const created_by = `Property ${hotel_id}`;

    dispatch(
      addFeedBackNote(id, note, created_by, success => {
        setAddNoteloading(false);

        if (success) {
          setNoteText('');
          dispatch(fetchfeedbackdetailsdata(feedback_id));
        }
      })
    );
  };

  const updateAlerts = (feedbackId, alertType, note, status, hotelId) => {
    if (!alertType?.length) return;
    status === 'closed' ? setCloseAlertloading(true) : setReopenAlertloading(true);
    const requests = alertType.map(type => ({
      feedback_id: feedbackId,
      alert_type: type,
      status,
      note,
      updated_by: `Property ${hotelId}`
    }));
    dispatch(
      updateAlert(requests, success => {
        status === 'closed' ? setCloseAlertloading(false) : setReopenAlertloading(false);

        if (success) {
          setAlertType([]);
          status === 'closed' ? setCloseAlertNoteText('') : setReopenAlertNoteText('');
          dispatch(fetchfeedbackdetailsdata(feedbackId));
        }
      })
    );
  };

  return (
    <ScrollView>
      <Card
        style={{
          container: {
            borderRadius: isMobile ? 0 : 20,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 12,
            elevation: 6,
            marginVertical: isMobile ? 0 : 8,
            marginHorizontal: isMobile ? 0 : 8
          }
        }}>
        {isLoading ? <FeedDetailsLoader /> : <>
        <View style={styles.headerContainer}>
          <View style={styles.ratingBadge}>
            <View style={{ marginRight: 8 }}>
              {feedbackType === 'Survey' ? (
                <EmojiMap type={getEmojiType(detail?.overall_rating)} />
              ) : feedbackType === 'Social media' && feedbackSource ? (
                   emojiSubIcon(feedbackSource, StyleSheet.flatten(styles.emojiSubIcon))
              ) : (
                <EmojiMap type={getEmojiType(detail?.overall_rating)} />
              )}
            </View>
            <Typography variant='h4' style={{ fontWeight: 'bold' }}>
              {Number(detail?.normalised_overall_rating)?.toString().replace(/\.0$/, '') || 0}
            </Typography>
            <Typography variant='h6' fontWeightType='regular'>
              /{Number(detail?.rating_scale)?.toString() || 5}
            </Typography>
            <Typography variant='h5' fontWeightType='regular' color='primaryOutline'>
              {' '}
              |{' '}
            </Typography>
          </View>

          <View style={styles.headerContent}>
            <View
              style={{
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'flex-start' : 'center'
              }}>
              <Typography variant='h5' fontWeightType='regular' numberOfLines={1}>
                {detail?.guest_name || 'Guest'}
              </Typography>

              <Typography
                variant='body2'
                style={{
                  color: '#555',
                  marginLeft: 8
                }}>
                <Typography
                  variant='body2'
                  style={{
                    color: '#555',
                    marginLeft: 8
                  }}>
                  {formatToLongDate(detail?.feedback_date)}
                </Typography>
              </Typography>
            </View>
          </View>
        </View>

        <View style={styles.buttonContainer}>
          <Button
            outlined
            title={'Add Notes'}
            style={{
              buttonContainer: {
                width: isMobile ? '50%' : '10%',
                borderColor: palette.hyperlinkBackgroundHigh
              },
              buttonText: {
                color: palette.hyperlinkBackgroundHigh,
                fontWeight: '600'
              }
            }}
            onClick={() => setNoteVisible(true)}
          />
          {hasOpenAlert && (
            <Button
              outlined
              title={'Close Alert'}
              style={{
                buttonContainer: {
                  width: isMobile ? '50%' : '10%',
                  borderColor: palette.hyperlinkBackgroundHigh
                },
                buttonText: {
                  color: palette.hyperlinkBackgroundHigh,
                  fontWeight: '600'
                }
              }}
              onClick={() => setCloseAlertVisible(true)}
            />
          )}
          {hasClosedAlert && (
            <Button
              outlined
              title={'Reopen Alert'}
              style={{
                buttonContainer: {
                  width: isMobile ? '50%' : '10%',
                  borderColor: palette.hyperlinkBackgroundHigh
                },
                buttonText: {
                  color: palette.hyperlinkBackgroundHigh,
                  fontWeight: '600'
                }
              }}
              onClick={() => setReopenAlertVisible(true)}
            />
          )}
        </View>

        <Modal transparent visible={noteVisible} animationType='fade' onRequestClose={() => setNoteVisible(false)}>
          <View style={styles.modalOverlay}>
            <View style={styles.modalContainer}>
              <Typography variant='h6'>Add Note</Typography>

              <TextInput
                value={noteText}
                onChangeText={setNoteText}
                placeholder='Type your note here...'
                style={[styles.input, { height: Math.min(400, noteText.split('\n').length * 20) }]}
                multiline={true}
                maxLength={1000}
              />
              <Typography variant='body2' fontWeightType='regular'>
                {noteText.length}/1000
              </Typography>
              <View style={styles.modalButtonRow}>
                <Button
                  title='Cancel'
                  outlined
                  onClick={() => setNoteVisible(false)}
                  style={{
                    buttonContainer: {
                      borderColor: '#2f81f7',
                      height: 40,
                      paddingHorizontal: 24,
                      borderRadius: 8,
                      width: '50%'
                    },
                    buttonText: {
                      color: '#2f81f7',
                      fontWeight: '600'
                    }
                  }}
                />

                <Button
                  title='Add'
                  loading={!note_response && addNoteLoading}
                  disabled={!noteText.trim()}
                  onClick={() => {
                    setNoteText(noteText);
                    addNote(feedback_id, noteText, detail?.survey_info?.hotel_id);
                    setNoteVisible(false);
                  }}
                  style={{
                    buttonContainer: {
                      backgroundColor: noteText.trim() ? '#2f81f7' : '#e0e0e0',
                      height: 40,
                      paddingHorizontal: 24,
                      borderRadius: 8,
                      marginLeft: 10,
                      width: '50%'
                    },
                    buttonText: {
                      color: noteText.trim() ? '#fff' : '#999',
                      fontWeight: '600'
                    }
                  }}
                />
              </View>
            </View>
          </View>
        </Modal>

        <Modal
          transparent
          visible={closeAlertVisible}
          animationType='fade'
          onRequestClose={() => setCloseAlertVisible(false)}>
          <View style={styles.modalOverlay}>
            <View style={styles.modalContainer}>
              <Typography variant='h6'>Close Alert</Typography>
              <View
                style={{
                  borderRadius: 8,
                  padding: 2,
                  marginTop: 15
                }}>
                <Dropdown
                  multi
                  style={{
                    container: {
                      borderColor: '#2f81f7'
                    }
                  }}
                  items={closeAlertOptions}
                  label='Alerts'
                  value={alertType}
                  onChange={value => {
                    setAlertType(value);
                  }}
                  labelKey='label'
                  valueKey='value'
                />
              </View>
              <TextInput
                value={closeAlertNoteText}
                onChangeText={setCloseAlertNoteText}
                placeholder='Type your note here...[Optional]'
                style={[
                  styles.input,
                  {
                    height: Math.min(400, closeAlertNoteText.split('\n').length * 20)
                  }
                ]}
                multiline
                maxLength={1000}
              />

              <Typography variant='body2' fontWeightType='regular'>
                {closeAlertNoteText.length}/1000
              </Typography>

              <View style={styles.modalButtonRow}>
                <Button
                  title='Cancel'
                  outlined
                  onClick={() => {
                    setCloseAlertVisible(false);
                    setAlertType('');
                    setCloseAlertNoteText('');
                  }}
                  style={{
                    buttonContainer: {
                      borderColor: '#2f81f7',
                      height: 40,
                      paddingHorizontal: 24,
                      borderRadius: 8,
                      width: '50%'
                    },
                    buttonText: {
                      color: '#2f81f7',
                      fontWeight: '600'
                    }
                  }}
                />

                <Button
                  title='Close Alert'
                  loading={!alert_response && closeAlertloading}
                  disabled={!alertType.length}
                  onClick={() => {
                    setCloseAlertNoteText(closeAlertNoteText);
                    updateAlerts(feedback_id, alertType, closeAlertNoteText, 'closed', detail?.survey_info?.hotel_id);
                    setCloseAlertVisible(false);
                    setAlertType('');
                  }}
                  style={{
                    buttonContainer: {
                      backgroundColor: !alertType ? '#e0e0e0' : '#2f81f7',
                      height: 40,
                      paddingHorizontal: 24,
                      borderRadius: 8,
                      marginLeft: 10,
                      width: '50%'
                    },
                    buttonText: {
                      color: !alertType ? '#999' : '#fff',
                      fontWeight: '600'
                    }
                  }}
                />
              </View>
            </View>
          </View>
        </Modal>

        <Modal
          transparent
          visible={reopenAlertVisible}
          animationType='fade'
          onRequestClose={() => setReopenAlertVisible(false)}>
          <View style={styles.modalOverlay}>
            <View style={styles.modalContainer}>
              <Typography variant='h6'>Add Note</Typography>
              <View
                style={{
                  borderRadius: 8,
                  padding: 2,
                  marginTop: 15
                }}>
                <Dropdown
                  multi
                  style={{
                    container: {
                      borderColor: '#2f81f7'
                    }
                  }}
                  items={reopenAlertOptions}
                  label='Alerts'
                  value={alertType}
                  onChange={value => {
                    setAlertType(value);
                  }}
                  labelKey='label'
                  valueKey='value'
                />
              </View>
              <TextInput
                value={reopenAlertNoteText}
                onChangeText={setReopenAlertNoteText}
                placeholder='Type your note here...[Optional]'
                style={[styles.input, { height: Math.min(400, noteText.split('\n').length * 20) }]}
                multiline={true}
                maxLength={1000}
              />
              <Typography variant='body2' fontWeightType='regular'>
                {reopenAlertNoteText.length}/1000
              </Typography>
              <View style={styles.modalButtonRow}>
                <Button
                  title='Cancel'
                  outlined
                  onClick={() => {
                    setReopenAlertVisible(false), setAlertType('');
                  }}
                  style={{
                    buttonContainer: {
                      borderColor: '#2f81f7',
                      height: 40,
                      paddingHorizontal: 24,
                      borderRadius: 8,
                      width: '50%'
                    },
                    buttonText: {
                      color: '#2f81f7',
                      fontWeight: '600'
                    }
                  }}
                />

                <Button
                  title='Reopen Alert'
                  loading={!alert_response && reopenAlertloading}
                  disabled={!alertType.length}
                  onClick={() => {
                    setReopenAlertNoteText(reopenAlertNoteText);
                    updateAlerts(feedback_id, alertType, reopenAlertNoteText, 'new', detail?.survey_info?.hotel_id);
                    setReopenAlertVisible(false);
                    setAlertType('');
                  }}
                  style={{
                    buttonContainer: {
                      backgroundColor: !alertType.length ? '#e0e0e0' : '#2f81f7',
                      height: 40,
                      paddingHorizontal: 24,
                      borderRadius: 8,
                      marginLeft: 10,
                      width: '50%'
                    },
                    buttonText: {
                      color: !alertType ? '#999' : '#fff',
                      fontWeight: '600'
                    }
                  }}
                />
              </View>
            </View>
          </View>
        </Modal>

        <View style={styles.cardContentWrapper}>
          <View style={styles.profileSection}>
            {(normalizedFeedBackType === 'survey' || normalizedFeedBackType === 'websurvey') && (
              <Typography variant='h6' fontWeightType='semiBold'>
                Profile
              </Typography>
            )}

            {(normalizedFeedBackType === 'survey' || normalizedFeedBackType === 'websurvey') && (
              <View style={styles.infoRow}>
                <View style={styles.leftColumn}>
                  <InfoSection title='PERSONAL INFORMATION' fields={fields} data={detail.personal_info} />
                </View>

                <View style={styles.rightColumn}>
                  <InfoSection
                    title='SURVEY INFORMATION'
                    fields={feedback_widget_details?.contents?.info_section?.survey_info}
                    data={detail.survey_info}
                  />
                </View>
              </View>
            )}

            {normalizedFeedBackType === 'socialmedia' && (
              <InfoSection
                title='Social Information'
                fields={feedback_widget_details?.contents?.info_section?.social_media_info}
                data={detail.social_media_info}
              />
            )}
          </View>

          <View style={styles.section}>
            <Typography variant='h6' fontWeightType='semiBold'>
              Activity
            </Typography>
            <View style={{ marginTop: 30 }}>
              <Activities activities={detail?.activity?.activities} />
            </View>
          </View>

          {hasSegments && (
            <View style={styles.section}>
              <Typography variant='h6' fontWeightType='semiBold'>
                Segments
              </Typography>
              <View style={{ marginTop: 20 }}>
                <View style={styles.segmentsContent}>
                  {Object.entries(segments).map(([key, value]) => (
                    <View key={key} style={styles.segmentRow}>
                      <View style={{ width: 400 }}>
                        <Typography variant='body2' fontWeightType='regular' style={styles.segmentLabel}>
                          {key
                            .replace(/_/g, ' ')
                            .split(' ')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ')}
                          ?
                        </Typography>
                      </View>

                      <View style={styles.segmentButtons}>
                        <View style={[styles.segmentButton, value === true && styles.activeButton]}>
                          <Typography
                            variant='body2'
                            fontWeightType='regular'
                            style={[styles.buttonText, value === true && styles.activeText]}>
                            ✓ Yes
                          </Typography>
                        </View>

                        <View style={[styles.segmentButton, value === false && styles.activeButton]}>
                          <Typography
                            variant='body2'
                            fontWeightType='regular'
                            style={[styles.buttonText, value === false && styles.activeText]}>
                            No
                          </Typography>
                        </View>
                      </View>
                    </View>
                  ))}
                </View>
              </View>
            </View>
          )}

          <View>
            {ratings && (
              <View style={{ paddingVertical: 16, paddingHorizontal: 20 }}>
                <Typography variant='h6' fontWeightType='semiBold' style={{ marginBottom: 10 }}>
                  Ratings
                </Typography>

                {Object.entries(ratings).map(([key, value]) => {
                  if (Array.isArray(blacklisted_ratings) && blacklisted_ratings.includes(key)) {
                    return null;
                  }
                  let maxValue = 5;

                  if (Array.isArray(rating_scale_ten) && rating_scale_ten.includes(key)) {
                    maxValue = 10;
                  }

                  const percentage = Math.round((value / maxValue) * 100);
                  let progressColor = 'positiveBackgroundHigh';
                  if (percentage < 50) progressColor = 'negativeContent';
                  else if (percentage < 70) progressColor = 'cautionBackgroundHigh';

                  const label = key
                    .replace(/_/g, ' ')
                    .split(' ')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');

                  return (
                    <View style={{ marginTop: 40 }}>
                      <View key={key} style={styles.ratingRow}>
                        {isMobile ? (
                          <View style={styles.mobileRatingHeaderRow}>
                            <Typography variant='body2' fontWeightType='regular' style={styles.ratingLabel}>
                              {label}
                            </Typography>

                            <Typography variant='body2' style={styles.mobileRatingValue}>
                              {value}
                            </Typography>
                          </View>
                        ) : (
                          <>
                            <View style={{ width: 220 }}>
                              <Typography variant='body2' fontWeightType='regular' style={styles.ratingLabel}>
                                {label}
                              </Typography>
                            </View>

                            <View style={styles.progressContainer}>
                              <ProgressBar
                                completed={percentage}
                                progressColor={progressColor}
                                style={{
                                  height: 50,
                                  completedTextContainer: { display: 'none' }
                                }}
                              />
                            </View>

                            <View style={styles.valueContainer}>
                              <Typography variant='body2' style={styles.ratingValue}>
                                {value}
                              </Typography>
                            </View>
                          </>
                        )}

                        {isMobile && (
                          <View style={[styles.progressContainer, { marginTop: 10 }]}>
                            <ProgressBar
                              completed={percentage}
                              progressColor={progressColor}
                              style={{
                                height: 50,
                                completedTextContainer: { display: 'none' }
                              }}
                            />
                          </View>
                        )}
                      </View>
                    </View>
                  );
                })}
              </View>
            )}
          </View>

          {detail?.comments && detail.comments.has_comment && (
            <View
              style={{
                paddingVertical: 16,
                paddingHorizontal: 20,
                borderTopWidth: 1,
                borderTopColor: '#f0f0f0',
                maxWidth: '90%'
              }}>
              <Typography variant='h6' style={{ fontWeight: 'bold', marginBottom: 16 }}>
                Comments
              </Typography>
              <View style={{ marginTop: 40 }}>
                <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 12 }}>
                  <Icons.MessageIcon color='primaryContentHigh' size='L' />

                  <View style={{ flex: 1, flexWrap: 'wrap' }}>
                    <Typography variant='subtitle1'>Comments</Typography>

                    <Typography
                      variant='body2'
                      fontWeightType='regular'
                      style={{
                        color: '#333',

                        flexWrap: 'wrap'
                      }}>
                      {detail?.comments?.improvement_comment || 'No comments available'}
                    </Typography>
                    <View style={{ marginTop: 30 }}>
                      <Typography
                        variant='caption'
                        style={{
                          color: '#666',
                          fontStyle: 'italic',
                          flexWrap: 'wrap'
                        }}>
                        Keywords: {detail?.comments?.all_survey_comments_concatenated}
                      </Typography>
                    </View>
                  </View>
                </View>
              </View>
            </View>
          )}
        </View> 
        </>}
      </Card>
    </ScrollView>
  );
};



export default FeedDetails;
